import { IntegrationsMapping } from "../../../../../integrations/IntegrationsMapping";

const updateSourcesAndLastSeen = (localUserInfo, tool, lastSeenTimestamp) => {
  if (!localUserInfo.sources.includes(tool)) localUserInfo.sources.push(tool);
  if (!lastSeenTimestamp) return;

  if (
    !localUserInfo.lastSeenTimestamp ||
    lastSeenTimestamp > localUserInfo.lastSeenTimestamp
  ) {
    localUserInfo.lastSeenTimestamp = lastSeenTimestamp;
    localUserInfo.last_seen_timestamp = lastSeenTimestamp;
  }
};

export const getLocalUserRows = (entityData) => {
  const localUserRowByUsername = {};
  /*
     structure: 
     {
        "the-username": { 
            username: "the-username", 
            lastSeenTimestamp: 123456, 
            isAdmin: false, 
            sources: ["crowdstrike"] 
        }
     }
     */

  const addLocalUser = (
    username,
    isAdmin,
    source,
    lastSeenTimestamp = null
  ) => {
    if (!username || !source) return;

    const existingUserInfo = localUserRowByUsername[username];
    const adminFlag = isAdmin === true;
    const normalizedLastSeen = lastSeenTimestamp ?? null;

    if (!existingUserInfo) {
      localUserRowByUsername[username] = {
        username,
        lastSeenTimestamp: normalizedLastSeen,
        last_seen_timestamp: normalizedLastSeen,
        isAdmin: adminFlag,
        sources: [source],
      };
      return;
    }

    existingUserInfo.isAdmin = existingUserInfo.isAdmin || adminFlag;
    updateSourcesAndLastSeen(existingUserInfo, source, normalizedLastSeen);
  };

  const localAdminUsers =
    entityData?.crowdstrike_data?.local_admin_users_compressed;
  if (localAdminUsers) {
    for (const localAdminUser of localAdminUsers) {
      addLocalUser(
        localAdminUser.username,
        true,
        IntegrationsMapping.crowdstrike.codeName,
        localAdminUser.last_seen_timestamp
      );
    }
  }

  const localNonAdminUsers =
    entityData?.crowdstrike_data?.local_non_admin_users_compressed;
  if (localNonAdminUsers)
    for (const localNonAdminUser of localNonAdminUsers) {
      addLocalUser(
        localNonAdminUser.username,
        false,
        IntegrationsMapping.crowdstrike.codeName,
        localNonAdminUser.last_seen_timestamp
      );
    }

  if (entityData?.jamf_data?.local_admin_usernames)
    for (const localAdminUsername of entityData.jamf_data
      .local_admin_usernames) {
      addLocalUser(localAdminUsername, true, IntegrationsMapping.jamf.codeName);
    }

  if (entityData?.jamf_data?.local_non_admin_usernames)
    for (const localNonAdminUsername of entityData.jamf_data
      .local_non_admin_usernames) {
      addLocalUser(
        localNonAdminUsername,
        false,
        IntegrationsMapping.jamf.codeName
      );
    }

  if (entityData?.jumpcloud_data?.local_user_admins)
    for (const localAdminUsername of entityData.jumpcloud_data
      .local_user_admins) {
      addLocalUser(
        localAdminUsername,
        true,
        IntegrationsMapping.jumpcloud.codeName
      );
    }

  if (entityData?.kandji_data?.local_admin_users)
    for (const localAdminUsername of entityData.kandji_data.local_admin_users) {
      addLocalUser(
        localAdminUsername,
        true,
        IntegrationsMapping.kandji.codeName
      );
    }

  if (entityData?.addigy_data?.local_user_admin)
    for (const localAdminUsername of entityData.addigy_data.local_user_admin) {
      addLocalUser(
        localAdminUsername,
        true,
        IntegrationsMapping.addigy.codeName
      );
    }

  if (entityData?.qualys_data?.local_admin_users)
    for (const localAdminUsername of entityData.qualys_data.local_admin_users) {
      addLocalUser(
        localAdminUsername,
        true,
        IntegrationsMapping.qualys.codeName
      );
    }

  if (entityData?.cloudconnexa_open_vpn_data?.access_users) {
    const accessUsersByGroup =
      entityData.cloudconnexa_open_vpn_data.access_users;

    for (const usernames of Object.values(accessUsersByGroup)) {
      if (Array.isArray(usernames)) {
        for (const username of usernames) {
          addLocalUser(
            username,
            false,
            IntegrationsMapping.cloudconnexa_open_vpn.codeName
          );
        }
      }
    }
  }

  if (entityData?.azure_vm_data?.local_admin_username)
    addLocalUser(
      entityData.azure_vm_data.local_admin_username,
      true,
      IntegrationsMapping.azure.codeName
    );

  return Object.values(localUserRowByUsername);
};

export const deviceHasAnyUsers = (entityData) => {
  const hasConnectedUsers =
    entityData?.microsoft_sccm_data?.top_console_user || entityData?.user_email;
  const hasLocalUsers = Boolean(
    getLocalUserRows(entityData).length > 0 ||
      entityData?.crowdstrike_data?.last_login_username
  );
  return hasConnectedUsers || hasLocalUsers;
};
